import React, { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { LogoSmall } from '@/resources/assets/images';
import StepsContainer from '../components/sequencesPage/StepsContainer';
import StepsContainerNav from '../components/sequencesPage/StepsContainerNav';
import SequencesContainerHeader from '../components/sequencesPage/SequencesContainerHeader';
import SequencesList from '../components/sequencesPage/SequencesList';
import { useSequencesContext } from '../SequencesContext';
import { SEQUENCES_PER_PAGE } from '../config';
import { useSequences } from '../hooks/useSequences';
import useStartSequence from '../hooks/useStartSequence';

export default function SequencesPage() {
	const { getPaginatedSequences } = useSequences();

	const paginatedSequences = getPaginatedSequences();

	const { selectedSequence } = useSequencesContext();

	useStartSequence();

	// Makes sure we have access to the user object to determine access
	const [user] = useSelector<RootState, [any]>(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	// Defines the terms of access. Currently, to avoid interruption,
	// it has a feature flag as well as allows access to all admins/VAs
	useEffect(() => {
		if (user && user.email) {
			if (
				user?.threadedEmail === '' &&
				!user.re2SharedDomain &&
				!user?.re2HostedEmail
			) {
				window.location.pathname = '/';
			}
		}
	}, [user.email]);

	const hasSelectedSequence = selectedSequence?.id !== undefined;

	return (
		<div
			style={{
				height: '100%'
			}}
			className="flex flex-col"
		>
			<div
				style={{
					height: '8%'
				}}
				className="mb-2"
			>
				<div className="flex h-full">
					<div className="flex w-2/5 items-center">
						<h1 className="mr-8 whitespace-nowrap text-3xl text-white">
							My sequences
						</h1>
						<p>
							Automate your outreach with a series of scheduled
							actions or emails.
						</p>
					</div>
					<div className="flex h-full w-3/5 items-center rounded-lg bg-dm-black px-4">
						<p className="font-bold">
							If a business has responded to your email, your
							sequence will be paused for that business.
						</p>
					</div>
				</div>
			</div>
			<div
				style={{
					height: '86%'
				}}
				className="flex flex-col"
			>
				<div className="flex h-24">
					<div className="w-2/5 pb-2 pr-2">
						<SequencesContainerHeader />
					</div>
					<div className="w-3/5 rounded-t-lg border-l border-r border-t border-dark-grey">
						<StepsContainerNav />
					</div>
				</div>
				<div className="flex h-full overflow-hidden rounded-l-lg border border-dark-grey">
					<div className="h-full w-2/5 overflow-y-auto">
						<SequencesList
							paginatedSequences={paginatedSequences}
						/>
					</div>
					<div className="h-full w-3/5 overflow-y-auto border-l border-dark-grey">
						{hasSelectedSequence ? (
							<StepsContainer />
						) : (
							<NoSequenceSelectedTooltip />
						)}
					</div>
				</div>
			</div>
			<div
				style={{
					maxHeight: '8%'
				}}
				className="mt-2 flex items-center"
			>
				<Pagination />
			</div>
		</div>
	);
}

function NoSequenceSelectedTooltip() {
	return (
		<div className="flex h-full flex-col gap-2 bg-dm-black p-6">
			<div className="flex items-center gap-3 rounded-2xl border border-[#757575] bg-[#303030] px-10 py-6">
				<div className="flex h-8 w-8 items-center justify-center">
					<LogoSmall />
				</div>
				<span className="text-lg font-semibold text-white">
					Create a new sequence, and preview each step of your
					sequence here
				</span>
			</div>
			<div className="h-full rounded-2xl border border-[#757575] bg-[#303030] p-6">
				<div className="flex flex-col gap-3 rounded-2xl bg-dm-black3.5 px-4 py-8 text-base">
					<span className="text-dm-white">
						Start a sequence quickly with one of our expertly
						crafted pre-made templates and adapt as needed.
					</span>
					<span className="text-dm-white">
						Add personalized messages, set follow-up timings, and
						ensure your sequence is tailored to your audience.
					</span>
				</div>
			</div>
		</div>
	);
}

function Pagination() {
	const { currentPage, setCurrentPage, sequences } = useSequencesContext();

	const totalPages = Math.ceil((sequences?.length || 0) / SEQUENCES_PER_PAGE);

	const pageNumbers = getPaginationRange(currentPage, totalPages);

	const nextPage = () => {
		setCurrentPage(currentPage + 1);
	};

	const previousPage = () => {
		setCurrentPage(currentPage - 1);
	};

	useEffect(() => {
		// Paginate back when deleting the last sequence of a page
		if (currentPage > 1 && currentPage > totalPages) {
			previousPage();
		}
	}, [sequences]);

	if (pageNumbers.length === 0) return null;

	return (
		<div className="my-auto flex items-center gap-3">
			<button onClick={previousPage} disabled={currentPage === 1}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="h-6 w-6 text-orange disabled:text-dark-grey"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M15.75 19.5 8.25 12l7.5-7.5"
					/>
				</svg>
			</button>
			{pageNumbers.map((page, index) => {
				if (page === -1) {
					return <div key={index}>...</div>;
				}
				return (
					<PaginationButton
						key={index}
						pageNumber={page}
						isSelected={currentPage === page}
						setCurrentPage={setCurrentPage}
					/>
				);
			})}

			<button onClick={nextPage} disabled={currentPage === totalPages}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="h-6 w-6 text-orange disabled:text-dark-grey"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="m8.25 4.5 7.5 7.5-7.5 7.5"
					/>
				</svg>
			</button>
		</div>
	);
}

function PaginationButton({
	pageNumber,
	isSelected,
	setCurrentPage
}: {
	pageNumber: number;
	isSelected: boolean;
	setCurrentPage: (page: number) => void;
}) {
	function handleClick() {
		if (isSelected) return;
		setCurrentPage(pageNumber);
	}

	return (
		<button
			onClick={handleClick}
			className={`flex h-10 w-10 items-center justify-center rounded-full font-bold hover:border hover:border-orange hover:text-orange ${isSelected && 'border border-orange text-orange'} `}
		>
			{pageNumber}
		</button>
	);
}

function getPaginationRange(currentPage: number, maxPages: number) {
	const range = [];
	const delta = 2; // Number of pages before and after the current page

	// Define the window range
	const left = currentPage - delta;
	const right = currentPage + delta;

	for (let i = 1; i <= maxPages; i++) {
		if (i === 1 || i === maxPages) {
			range.push(i);
			continue;
		}
		if (i === left || i === right) {
			range.push(-1);
			continue;
		}
		if (i < left || i > right) {
			continue;
		}
		if (i > right) {
			range.push(i);
			continue;
		}
		range.push(i);
	}

	return range;
}
